import { useState, useRef, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { isAdmin, isAccountant, handleError, downloadFile, API_URL } from "src/Common/function";
import BranchBar from "src/Components/branchbar/BranchBar";
import BlueButton from "src/Components/Common/bluebutton";
import DownloadExcelButton from "src/Components/Common/downloadexcelbutton";
import UserService from "src/Services/API/user.service";
import Invite from "./Invite/invite";
import Manage from "./Manage/manage";
import Archive from "./Archive/archive";
import Status from "./status/Status";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const TAB_SLUGS = ["manage", "invite", "archive", "status"];

const Team = () => {
  const navigate = useNavigate();
  const { tab: tabSlug } = useParams();
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));
  const currentUser = UserService.getCurrentUser();

  const tabIndex = Math.max(0, TAB_SLUGS.indexOf(tabSlug));
  const [isDownloadExcelLoading, setIsDownloadExcelLoading] = useState(false);
  const [inviteModalShown, setInviteModalShown] = useState(false);

  const tabRefs = useRef([]);
  const [tabUnderline, setTabUnderline] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measureUnderline = () => {
      const activeTab = tabRefs.current[tabIndex];
      if (activeTab) {
        setTabUnderline({ left: activeTab.offsetLeft, width: activeTab.offsetWidth });
      }
    };

    measureUnderline();

    // Re-measure once webfonts finish loading: on a fresh page load the custom
    // font may not be ready when we first measure, so the tab text reflows after
    // and the underline ends up mispositioned.
    if (document.fonts?.ready) {
      document.fonts.ready.then(measureUnderline);
    }

    window.addEventListener("resize", measureUnderline);
    return () => window.removeEventListener("resize", measureUnderline);
  }, [tabIndex]);

  const downloadExcel = async () => {
    setIsDownloadExcelLoading(true);
    try {
      let response = await UserService.generateExcelForTeamManage(branch);
      if (response.status == 200) {
        await downloadFile(`${API_URL}/${currentUser.company}/reports/${response.data}`, response.data);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadExcelLoading(false);
    }
  };

  const tabClicked = (index) => {
    const query = searchParams.toString();
    navigate(`/team/${TAB_SLUGS[index]}${query ? `?${query}` : ""}`, { replace: true });
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="py-4 border-b border-[#E5E8F3] flex-shrink-0 px-7">
        <BranchBar />
      </div>
      <div className="flex items-center justify-between border-b border-[#E5E8F3] flex-shrink-0 px-7">
        <div className="relative flex items-center gap-5 py-3 mt-3">
          {["Manage", "Invite", "Archive", "Status"].map((label, index) => (
            <span
              key={label}
              ref={(el) => (tabRefs.current[index] = el)}
              onClick={() => tabClicked(index)}
              className={`font-OpenSansRegular text-[14px] font-[600] leading-[20px] tracking-[0.14px] text-center capitalize cursor-pointer pb-3 -mb-3 transition-colors duration-300 ${
                tabIndex === index ? "text-blueColor" : "text-[#7C829A]"
              }`}
            >
              {label}
            </span>
          ))}
          <span
            className="absolute bottom-0 h-[2px] bg-blueColor transition-all duration-300 ease-out"
            style={{
              left: tabUnderline.left,
              width: tabUnderline.width,
            }}
          />
        </div>
        {tabIndex === 0 && <DownloadExcelButton onClicked={downloadExcel} isLoading={isDownloadExcelLoading} />}
        {tabIndex === 1 && (isAdmin() || isAccountant()) && (
          <BlueButton title="INVITE USER" onClicked={() => setInviteModalShown(true)} />
        )}
      </div>
      <div key={tabIndex} className="animate-tabFadeIn flex-1 min-h-0 px-7">
        {tabIndex === 0 ? (
          <Manage branch={branch} />
        ) : tabIndex === 1 ? (
          <Invite branch={branch} inviteModalShown={inviteModalShown} setInviteModalShown={setInviteModalShown} />
        ) : tabIndex === 2 ? (
          <Archive branch={branch} />
        ) : (
          <Status branch={branch} />
        )}
      </div>
    </div>
  );
};

export default Team;
